import React, { createContext, useContext, ReactNode, useEffect, useState } from 'react';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { apiClient } from '../services/api';
import { useAuth } from './AuthContext';

interface StripeContextType {
  stripe: Stripe | null;
  publishableKey: string | null;
  isLoading: boolean;
}

const StripeContext = createContext<StripeContextType | undefined>(undefined);

export function StripeProvider({ children }: { children: ReactNode }) {
  const { user, isLoading: authLoading } = useAuth();
  const [stripe, setStripe] = useState<Stripe | null>(null);
  const [publishableKey, setPublishableKey] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const isPlatformHost =
      typeof window !== 'undefined' &&
      window.location?.hostname?.toLowerCase().startsWith('globaladmin.');

    if (authLoading) {
      setIsLoading(true);
      return;
    }

    if (isPlatformHost || !user) {
      setStripe(null);
      setPublishableKey(null);
      setIsLoading(false);
      return;
    }

    void loadStripeConfig();
  }, [authLoading, user?.id]);

  const loadStripeConfig = async () => {
    setIsLoading(true);

    try {
      const response = await apiClient.get('/payments/config');
      const { publishableKey: key } = response.data;
      
      if (key) {
        setPublishableKey(key);
        const stripeInstance = await loadStripe(key);
        setStripe(stripeInstance);
      } else {
        setPublishableKey(null);
        setStripe(null);
      }
    } catch (error: any) {
      setPublishableKey(null);
      setStripe(null);

      const status = error?.response?.status;

      // Stripe is optional and this endpoint is only valid for authenticated company tenants.
      if (status !== 401 && status !== 403 && status !== 404) {
        console.warn('Stripe configuration not available - payment features disabled');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Always render children, even if Stripe is not configured
  // Some features work without Stripe (like invoices)
  if (isLoading) {
    return <div>{children}</div>;
  }

  // If no publishableKey, render without Elements wrapper (Stripe not configured)
  if (!publishableKey || !stripe) {
    return (
      <StripeContext.Provider value={{ stripe: null, publishableKey: null, isLoading: false }}>
        {children}
      </StripeContext.Provider>
    );
  }

  return (
    <StripeContext.Provider value={{ stripe, publishableKey, isLoading }}>
      <Elements stripe={stripe}>
        {children}
      </Elements>
    </StripeContext.Provider>
  );
}

export function useStripe() {
  const context = useContext(StripeContext);
  if (context === undefined) {
    throw new Error('useStripe must be used within a StripeProvider');
  }
  return context;
}